import React, { useEffect } from 'react';
import { Briefcase, ArrowRight, Mail } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';

// Lists DealTeams the partner is an active member of, with seller name
// and the partner's role on the team. Each card links into the existing
// DealTeamWorkspace, where the chrome adapts based on viewer.role.
const ROLE_LABELS = {
  cbv: 'Valuation Specialist (CBV)',
  business_broker: 'Business Broker',
  lender: 'Lender',
  cepa_coach: 'Exit Planner (CEPA)',
  accountant: 'Accountant',
  lawyer: 'Lawyer',
};

function deriveInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function PartnerEngagements({
  engagements = [],
  loading = false,
  persona = 'PARTNER',
  userName,
  userInitials,
  planProgress,
  actionPlanBadge,
  onAcceptInvite,
  accepting = false,
  acceptError = null,
}) {
  useEffect(() => {
    if (typeof window.ahoy !== 'undefined') {
      window.ahoy.track('succeedo_partner_engagements_list_viewed', {
        count: engagements.length,
      });
    }
  }, [engagements.length]);

  return (
    <AppLayout
      breadcrumb="MY ENGAGEMENTS"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      isPartner
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="max-w-5xl mx-auto px-8 py-12">
        <div className="mb-8">
          <h1 className="font-serif font-light text-[32px] text-foreground mb-2">My Engagements</h1>
          <p className="text-[14px] text-muted-foreground max-w-[640px]">
            Active deal teams you've been added to. Click an engagement to coordinate with the seller and other advisors.
          </p>
        </div>

        {loading && (
          <div className="text-[14px] text-muted-foreground">Loading…</div>
        )}

        {!loading && engagements.length === 0 && (
          <div className="border border-border rounded-[12px] p-10 text-center bg-card">
            <Briefcase className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <h2 className="font-medium text-[18px] text-foreground mb-1">No active engagements yet</h2>
            <p className="text-[14px] text-muted-foreground max-w-[440px] mx-auto">
              When a seller adds you to their deal team, you'll see their workspace here. Accepted introductions don't automatically add you to a team — the seller decides who to bring on.
            </p>
          </div>
        )}

        {acceptError && (
          <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-[13px] px-3 py-2">
            {acceptError}
          </div>
        )}

        {!loading && engagements.length > 0 && (
          <div className="space-y-3">
            {engagements.map((e) => {
              const invited = e.member_status === 'invited';
              const cardClasses = invited
                ? 'bg-card border-2 border-[hsl(var(--succeedo-orange))]/30 rounded-[12px] p-5'
                : 'block bg-card border border-border rounded-[12px] p-5 hover:border-[hsl(var(--succeedo-orange))]/40 transition-colors';

              const handleAccept = async (event) => {
                event.preventDefault();
                if (!onAcceptInvite) return;
                await onAcceptInvite({ memberId: e.member_id, token: e.invite_token });
              };

              const Inner = (
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-muted text-foreground flex items-center justify-center font-mono text-[12px] font-medium shrink-0">
                    {deriveInitials(e.seller_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-medium text-foreground truncate flex items-center gap-2">
                      {e.team_name}
                      {invited && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide rounded-full px-2 py-0.5 bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))]">
                          <Mail className="w-3 h-3" />
                          Invitation
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-muted-foreground truncate">
                      {e.seller_name} · You're the {ROLE_LABELS[e.my_role] || e.my_role}
                    </div>
                  </div>
                  {invited ? (
                    <button
                      type="button"
                      onClick={handleAccept}
                      disabled={accepting || !e.invite_token}
                      className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
                    >
                      {accepting ? 'Accepting…' : 'Accept'}
                    </button>
                  ) : (
                    <ArrowRight className="w-5 h-5 text-muted-foreground/40 shrink-0" />
                  )}
                </div>
              );

              if (invited) {
                return (
                  <div key={e.member_id || e.team_id} className={cardClasses}>
                    {Inner}
                  </div>
                );
              }

              return (
                <a
                  key={e.member_id || e.team_id}
                  href={`/succeedo/teams/${e.team_id}/workspace`}
                  onClick={() => {
                    if (typeof window.ahoy !== 'undefined') {
                      window.ahoy.track('succeedo_partner_engagement_viewed', {
                        team_id: e.team_id,
                        role: e.my_role,
                      });
                    }
                  }}
                  className={cardClasses}
                >
                  {Inner}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
